// Meldungsbereich der Schadenmappe (Spec §1): ein Meldungsschritt nach dem
// anderen — dieselben Schritt-Komponenten wie der öffentliche Wizard
// (`PublicSchadenmeldungBig.tsx`), aber in der Reihenfolge der Mappe
// (`schrittListeIntern`): Kontakt → Schadenart → Risiko & Vertrag → Wo → Wann →
// … → Schadenmelder (ohne „für wen“) → Abschluss → Prüfen — und die feste
// Fußleiste mit Zurück/Weiter.
//
// Weiter/Zurück wechseln NUR den Schritt (`onSchritt`): Die Seite speichert
// beim Wechsel sofort, und das liest den zuletzt committeten Stand.
import * as React from "react";
import { themeVars } from "../../schadenmeldung/theme";
import { PhotoProvider } from "../../schadenmeldung-big/PhotoUpload";
import { WhenStep } from "../../schadenmeldung-big/steps/shared";
import {
  GebAbrechnung,
  GebArt,
  GebHergang,
  GebMassnahmen,
  GebVerursacher,
  GebWhere,
} from "../../schadenmeldung-big/steps/gebaeude";
import {
  KfzArt,
  KfzEigenes,
  KfzFremdes,
  KfzHergang,
  KfzPerson,
  KfzSachen,
  KfzSchaeden,
  KfzWhere,
} from "../../schadenmeldung-big/steps/kfz";
import { SonBetroffen, SonDetails, SonWhere } from "../../schadenmeldung-big/steps/sonstiges";
import type { BigAnswers, BigStepKey } from "../../schadenmeldung-big/types";
import { refinedTheme } from "./refinedTheme";
import { SchadenartIntern, type Verweis } from "./SchadenartIntern";
import { AbschlussIntern, type AbschlussWerte } from "./AbschlussIntern";
import { PruefenIntern } from "./PruefenIntern";
import { MelderIntern } from "./MelderIntern";
import { useWoAusRisiko } from "./useWoAusRisiko";
import { naechsterSchritt, schrittLabel, vorigerSchritt, type SchrittEintrag } from "./schritte";

export interface MeldungBereichProps {
  answers: BigAnswers;
  update: (patch: Partial<BigAnswers>) => void;
  schadensart: string | null;
  onSchadensart: (s: string | null) => void;
  kontakt: Verweis | null;
  onKontakt: (k: Verweis | null) => void;
  risiko: Verweis | null;
  onRisiko: (r: Verweis | null) => void;
  vertrag: Verweis | null;
  onVertrag: (v: Verweis | null) => void;
  /** Kontakt auf dem Server (`null` vor der Anlage) — Bezug für „anderer Kontakt“ und „Abbrechen“. */
  gespeicherterKontakt: Verweis | null;
  schritt: BigStepKey;
  onSchritt: (s: BigStepKey) => void;
  /** Meldung an den Versicherer (Spalten des Schadens, sofort gespeichert). */
  abschluss: AbschlussWerte;
  onAbschluss: (patch: Partial<AbschlussWerte>) => void;
  /** „heute“ als Meldedatum vorschlagen? (Je Schaden einmal, die Seite merkt es sich.) */
  meldedatumVorschlagen: boolean;
  /** Schritte mit unerfüllter Pflicht — für „Prüfen“. */
  fehlende: SchrittEintrag[];
  /** `false` vor der Anlage — dann gibt es nur „Kontakt“ und „Schadenart“. */
  angelegt: boolean;
  /** Die Anlage läuft: Eingaben und Fußleiste sind gesperrt. */
  anlageLaeuft: boolean;
  /** Für die Unterlagen (Task 10). */
  damageId: number | null;
  vorgangId: number | null;
  contactId: number | null;
}

/**
 * Fotos im Hergang: In der Mappe werden Dateien sofort hochgeladen (Task 10);
 * bis dahin nimmt der Provider nichts an.
 */
const KEINE_FOTOS = { files: [] as File[], addFiles: () => undefined, removeFile: () => undefined };

function Platzhalter({ schritt }: { schritt: BigStepKey }) {
  return <div className="smp-platzhalter">{schrittLabel(schritt)}</div>;
}

export function MeldungBereich(props: MeldungBereichProps) {
  const { answers, update, schritt, onSchritt, angelegt, anlageLaeuft, kontakt } = props;
  const stepProps = { answers, update };

  // „Wo“ wird beim Öffnen aus der Anschrift des gewählten Risikos vorbelegt,
  // solange Straße, PLZ und Ort leer sind (kein Hinweis — die Felder sind gefüllt).
  useWoAusRisiko({
    aktiv: schritt === "geb_where" || schritt === "son_where",
    risikoId: props.risiko?.id ?? null,
    answers,
    update,
  });

  let inhalt: React.ReactNode;
  switch (schritt) {
    case "kontakt":
    case "type":
    case "risiko":
      inhalt = (
        <SchadenartIntern
          teil={schritt === "type" ? "art" : schritt}
          answers={answers}
          update={update}
          schadensart={props.schadensart}
          onSchadensart={props.onSchadensart}
          kontakt={kontakt}
          onKontakt={props.onKontakt}
          risiko={props.risiko}
          onRisiko={props.onRisiko}
          vertrag={props.vertrag}
          onVertrag={props.onVertrag}
          gespeicherterKontakt={props.gespeicherterKontakt}
        />
      );
      break;

    case "geb_melder":
    case "kfz_melder":
    case "son_melder":
      inhalt = (
        <MelderIntern
          answers={answers}
          update={update}
          contactId={props.contactId}
          kontaktName={kontakt?.label ?? props.gespeicherterKontakt?.label ?? ""}
        />
      );
      break;
    case "geb_when":
    case "kfz_when":
    case "son_when":
      inhalt = <WhenStep {...stepProps} />;
      break;

    case "geb_where":
      inhalt = <GebWhere {...stepProps} />;
      break;
    case "geb_art":
      inhalt = <GebArt {...stepProps} />;
      break;
    case "geb_hergang":
      inhalt = <GebHergang {...stepProps} />;
      break;
    case "geb_massnahmen":
      inhalt = <GebMassnahmen {...stepProps} />;
      break;
    case "geb_verursacher":
      inhalt = <GebVerursacher {...stepProps} />;
      break;
    case "geb_abrechnung":
      inhalt = <GebAbrechnung {...stepProps} />;
      break;

    case "kfz_schaeden":
      inhalt = <KfzSchaeden {...stepProps} />;
      break;
    case "kfz_where":
      inhalt = <KfzWhere {...stepProps} />;
      break;
    case "kfz_art":
      inhalt = <KfzArt {...stepProps} />;
      break;
    case "kfz_eigenes":
      inhalt = <KfzEigenes {...stepProps} />;
      break;
    case "kfz_fremdes":
      inhalt = <KfzFremdes {...stepProps} />;
      break;
    case "kfz_hergang":
      inhalt = <KfzHergang {...stepProps} />;
      break;
    case "kfz_person":
      inhalt = <KfzPerson {...stepProps} />;
      break;
    case "kfz_sachen":
      inhalt = <KfzSachen {...stepProps} />;
      break;

    case "son_betroffen":
      inhalt = <SonBetroffen {...stepProps} />;
      break;
    case "son_where":
      inhalt = <SonWhere {...stepProps} />;
      break;
    case "son_details":
      inhalt = <SonDetails {...stepProps} />;
      break;

    case "abschluss":
      // Je Schaden frisch: der Vorschlag „heute“ gilt für jeden Schaden einmal.
      inhalt = (
        <AbschlussIntern
          key={props.damageId ?? "neu"}
          werte={props.abschluss}
          onAbschluss={props.onAbschluss}
          vorschlagErlaubt={props.meldedatumVorschlagen}
        />
      );
      break;
    case "summary":
      inhalt = (
        <PruefenIntern
          answers={answers}
          fehlende={props.fehlende}
          onSchritt={onSchritt}
          kontakt={kontakt ?? props.gespeicherterKontakt}
          risiko={props.risiko}
          vertrag={props.vertrag}
          schadensart={props.schadensart}
        />
      );
      break;

    // Unterlagen (Task 10).
    default:
      inhalt = <Platzhalter schritt={schritt} />;
  }

  const zurueck = vorigerSchritt(answers, schritt);
  const weiter = naechsterSchritt(answers, schritt);
  // Vor der Anlage gibt es nur „Kontakt“ und „Schadenart“: von „Kontakt“ geht es
  // weiter, sobald einer gewählt ist; auf „Schadenart“ startet die Anlage von
  // selbst, sobald Kontakt + Schadenart stehen — Weiter hätte dort kein Ziel
  // (auch nicht nach einer gescheiterten Anlage; die wiederholt die Kopfzeile).
  // Danach ist Weiter nie gesperrt.
  const weiterBereit = angelegt || (schritt === "kontakt" && kontakt !== null);

  return (
    <div style={themeVars(refinedTheme)} className="smp-schritt">
      <PhotoProvider value={KEINE_FOTOS}>
        <fieldset className="smp-schritt-feld" disabled={anlageLaeuft}>
          {inhalt}
        </fieldset>
      </PhotoProvider>
      <div className="smp-fuss">
        <div className="smp-fuss-innen">
          <button
            type="button"
            className="tbtn smp-fuss-knopf"
            disabled={anlageLaeuft || zurueck === null}
            onClick={() => zurueck && onSchritt(zurueck)}
          >
            ← Zurück
          </button>
          <button
            type="button"
            className="tbtn primary smp-fuss-knopf"
            disabled={anlageLaeuft || weiter === null || !weiterBereit}
            onClick={() => weiter && onSchritt(weiter)}
          >
            Weiter →
          </button>
        </div>
      </div>
    </div>
  );
}
